import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    X,
    Mail,
    UserPlus,
    Calendar,
    Save,
    FileText,
    Briefcase,
    GraduationCap,
    Building2,
    Users,
    Search,
    Check,
    Filter,
    ChevronDown,
    ChevronUp,
    Send,
} from "lucide-react";

import { getAllEntitesRequest } from "../../../../redux/actions/entitesActions";
import { getAllUsersRequest } from "../../../../redux/actions/userActions";
import { getAllJobsRequest } from "../../../../redux/actions/Jobactions";
import { 
    saveDemandeRecrutementCiblageRequest,
    sendDemandeRecrutementCiblageRequest 
} from "../../../../redux/actions/demandesRecrutementActions";

import MultiSelectDropdown from "../../../../Ui/UiJs/MultiSelectDropdown";
import "../../../Formation/ResponsableFormation/RessourcesStyles/CampagneManagement.css";
import "./Recommandationcooptation.css";

function InlineUsersPanel({
                              users,
                              usersState,
                              setUsersState,
                              setSelectedUsers,
                              entiteIds,
                              posteIds,
                          }) {
    const [search, setSearch] = useState("");
    const [autoFilter, setAutoFilter] = useState(false);

    const AVATAR_COLORS = ["#204385", "#ee645f", "#0891b2", "#059669", "#7c3aed", "#d97706"];
    const getAvatarColor = (id) => AVATAR_COLORS[+id % AVATAR_COLORS.length];

    const getInitials = (u) => {
        const first = (u.firstName || u.prenom || "?")[0];
        const last = (u.lastName || u.nom || "")[0] || "";
        return `${first}${last}`.toUpperCase();
    };

    const getFullName = (u) =>
        `${u.firstName || u.prenom || ""} ${u.lastName || u.nom || ""}`.trim() || `User #${u.id}`;

    const baseList = useMemo(() => {
        if (!autoFilter || (!entiteIds.length && !posteIds.length)) return usersState || [];

        return (usersState || []).filter((u) => {
            const matchEnt =
                entiteIds.length === 0 ||
                entiteIds.includes(+u.entiteId) ||
                entiteIds.includes(+u.entityId);

            const matchPost =
                posteIds.length === 0 ||
                posteIds.includes(+u.posteId) ||
                posteIds.includes(+u.jobId);

            return matchEnt && matchPost;
        });
    }, [usersState, autoFilter, entiteIds, posteIds]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return baseList;

        return baseList.filter((u) =>
            `${u.firstName || u.prenom || ""} ${u.lastName || u.nom || ""} ${u.email || ""}`
                .toLowerCase()
                .includes(q)
        );
    }, [baseList, search]);

    const toggleUser = (userId) => {
        const updated = usersState.map((u) =>
            +u.id === +userId ? { ...u, checked: !u.checked } : u
        );
        setUsersState(updated);
        setSelectedUsers(updated.filter((u) => u.checked));
    };

    const toggleAll = () => {
        const allChecked = filtered.every((u) => u.checked);
        const filteredIds = new Set(filtered.map((u) => +u.id));

        const updated = usersState.map((u) =>
            filteredIds.has(+u.id) ? { ...u, checked: !allChecked } : u
        );

        setUsersState(updated);
        setSelectedUsers(updated.filter((u) => u.checked));
    };

    const clearAll = () => {
        const updated = usersState.map((u) => ({ ...u, checked: false }));
        setUsersState(updated);
        setSelectedUsers([]);
    };

    const checkedUsers = (usersState || []).filter((u) => u.checked);
    const allFilteredChecked = filtered.length > 0 && filtered.every((u) => u.checked);
    const hasAutoFilterOption = entiteIds.length > 0 || posteIds.length > 0;

    return (
        <div className="cr-users-panel">
            <div className="cr-users-panel__header">
                <div className="cr-users-panel__title">
                    <Users size={14} />
                    Collaborateurs
                </div>

                <div className="cr-users-header-right">
                    {hasAutoFilterOption && (
                        <button
                            type="button"
                            className={`cr-auto-filter-btn${autoFilter ? " active" : ""}`}
                            onClick={() => setAutoFilter((p) => !p)}
                            title="Filtrer selon les entités et postes sélectionnés"
                        >
                            <Filter size={12} />
                            Filtrer par entités/postes
                        </button>
                    )}

                    <div className="cr-users-search">
                        <Search size={12} className="cr-users-search-icon" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Rechercher..."
                            className="cr-users-search-input"
                        />
                    </div>
                </div>
            </div>

            {checkedUsers.length > 0 && (
                <div className="cr-users-summary">
                    <div className="cr-avatar-stack">
                        {checkedUsers.slice(0, 5).map((u) => (
                            <div
                                key={u.id}
                                className="cr-avatar-stack__item"
                                style={{ background: getAvatarColor(u.id) }}
                                title={getFullName(u)}
                            >
                                {getInitials(u)}
                            </div>
                        ))}
                        {checkedUsers.length > 5 && (
                            <div className="cr-avatar-stack__more">+{checkedUsers.length - 5}</div>
                        )}
                    </div>

                    <span className="cr-users-summary__label">
                        <strong>{checkedUsers.length}</strong> collaborateur
                        {checkedUsers.length > 1 ? "s" : ""} sélectionné
                        {checkedUsers.length > 1 ? "s" : ""}
                    </span>

                    <button
                        type="button"
                        className="cr-users-summary__clear"
                        onClick={clearAll}
                    >
                        <X size={11} /> Tout désélectionner
                    </button>
                </div>
            )}

            {(usersState || []).length === 0 ? (
                <div className="cr-users-empty">
                    <Users size={28} style={{ color: "#d1d5db", marginBottom: 8 }} />
                    <div>Aucun utilisateur disponible</div>
                </div>
            ) : filtered.length === 0 ? (
                <div className="cr-users-empty">
                    Aucun résultat{search ? ` pour « ${search} »` : " avec ce filtre"}
                </div>
            ) : (
                <>
                    <div className="cr-users-select-all" onClick={toggleAll}>
                        <div className={`cr-user-checkbox${allFilteredChecked ? " checked" : ""}`}>
                            {allFilteredChecked && <Check size={10} />}
                        </div>
                        <span className="cr-users-select-text">
                            {allFilteredChecked ? "Tout désélectionner" : "Tout sélectionner"} (
                            {filtered.length})
                        </span>
                    </div>

                    <div className="cr-users-list">
                        {filtered.map((u) => (
                            <div
                                key={u.id}
                                className={`cr-user-row${u.checked ? " checked" : ""}`}
                                onClick={() => toggleUser(u.id)}
                            >
                                <div className={`cr-user-checkbox${u.checked ? " checked" : ""}`}>
                                    {u.checked && <Check size={10} />}
                                </div>

                                <div
                                    className="cr-user-avatar"
                                    style={
                                        u.checked
                                            ? { background: getAvatarColor(u.id), color: "#fff" }
                                            : {}
                                    }
                                >
                                    {getInitials(u)}
                                </div>

                                <div className="cr-user-info">
                                    <div className="cr-user-name">{getFullName(u)}</div>
                                    {u.email && <div className="cr-user-email">{u.email}</div>}
                                </div>

                                {u.poste && (
                                    <span
                                        className="cr-badge cr-badge--grey"
                                        style={{ fontSize: 10, marginLeft: "auto" }}
                                    >
                                        {u.poste}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

const RecommandationCooptationModal = ({ open, onClose, demande }) => {
    const dispatch = useDispatch();

    const entites = useSelector((state) => state.entite?.entites || []);
    const jobs = useSelector((state) => state.jobs?.jobs || []);
    const users = useSelector((state) => state.user?.users || state.users?.users || []);

    const [activeMode, setActiveMode] = useState("recommandation");
    const [envoiDate, setEnvoiDate] = useState(new Date().toISOString().split("T")[0]);
    const [limitDate, setLimitDate] = useState(
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
    );

    const [filterMode, setFilterMode] = useState("IN");
    const [entiteIds, setEntiteIds] = useState([]);
    const [posteIds, setPosteIds] = useState([]);
    const [usersState, setUsersState] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showUsersPanel, setShowUsersPanel] = useState(false);

    useEffect(() => {
        if (!open) return;

        dispatch(getAllEntitesRequest());
        dispatch(getAllJobsRequest());
        dispatch(getAllUsersRequest());
    }, [dispatch, open]);

    useEffect(() => {
        if (!open) return;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = "unset";
        };
    }, [open]);

    useEffect(() => {
        const next = (users || []).map((u) => ({
            ...u,
            checked: false,
        }));
        setUsersState(next);
        setSelectedUsers([]);
    }, [users]);

    useEffect(() => {
        if (!open) return;

        setActiveMode("recommandation");
        setEnvoiDate(new Date().toISOString().split("T")[0]);
        setLimitDate(
            new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
        );
        setFilterMode("IN");
        setEntiteIds([]);
        setPosteIds([]);
        setShowUsersPanel(false);
    }, [open, demande?.id]);

    if (!open || !demande) return null;

    const getEntiteName = (id) => {
        const entite = (entites || []).find((e) => +e.id === +id);
        return entite ? entite.nom || entite.name : `#${id}`;
    };

    const getPosteName = (id) => {
        const job = (jobs || []).find((j) => +j.id === +id);
        return job ? job.nom || job.name : `#${id}`;
    };

    const handleSave = () => {
        const payload = {
            sourceType: activeMode === "recommandation" ? "RECOMMENDATION" : "COOPTATION",
            envoiDate,
            limitDate,
            filterMode,
            entiteIds: entiteIds.map((id) => +id),
            posteIds: posteIds.map((id) => +id),
            userIds: selectedUsers.map((u) => +u.id),
        };

        dispatch(saveDemandeRecrutementCiblageRequest(demande.id, payload));
        onClose();
    };

    const handleSendNow = () => {
        const payload = {
            sourceType: activeMode === "recommandation" ? "RECOMMENDATION" : "COOPTATION",
            envoiDate,
            limitDate,
            filterMode,
            entiteIds: entiteIds.map((id) => +id),
            posteIds: posteIds.map((id) => +id),
            userIds: selectedUsers.map((u) => +u.id),
            sendImmediately: true
        };

        // Save and trigger send atomically in one backend call
        dispatch(saveDemandeRecrutementCiblageRequest(demande.id, payload));
        
        onClose();
    };

    const title =
        activeMode === "recommandation"
            ? "Email de recommandation"
            : "Email de cooptation";

    return (
        <div className="rcModal" onMouseDown={onClose} role="dialog" aria-modal="true">
            <div className="rcModal__panel rcModal__panel--wide" onMouseDown={(e) => e.stopPropagation()}>
                <div className="rcModal__header">
                    <div className="rcModal__title">
                        {activeMode === "recommandation" ? <Mail size={18} /> : <UserPlus size={18} />}
                        {title}
                    </div>

                    <button className="rcIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={18} />
                    </button>
                </div>

                <div className="rcTabs">
                    <button
                        type="button"
                        className={`rcTab ${activeMode === "recommandation" ? "rcTab--active" : ""}`}
                        onClick={() => setActiveMode("recommandation")}
                    >
                        <Mail size={16} />
                        <span>Recommandation</span>
                    </button>

                    <button
                        type="button"
                        className={`rcTab ${activeMode === "cooptation" ? "rcTab--active" : ""}`}
                        onClick={() => setActiveMode("cooptation")}
                    >
                        <UserPlus size={16} />
                        <span>Cooptation</span>
                    </button>
                </div>

                <div className="rcModal__body">
                    <div className="rcSection">
                        <div className="rcSection__title">
                            <FileText size={16} />
                            Demande concernée
                        </div>

                        <div className="rcInfoGrid">
                            <div className="rcInfoItem">
                                <span className="rcInfoLabel">Campagne</span>
                                <span className="rcInfoValue">{demande.campagneNom || "—"}</span>
                            </div>

                            <div className="rcInfoItem">
                                <span className="rcInfoLabel">Poste</span>
                                <span className="rcInfoValue">
                                    {demande.posteNom || demande.poste || demande.autrePoste || "—"}
                                </span>
                            </div>

                            <div className="rcInfoItem">
                                <span className="rcInfoLabel">Niveau d'étude</span>
                                <span className="rcInfoValue">
                                    {demande.niveauEtude || demande.niveauxEtudes || "—"}
                                </span>
                            </div>

                            <div className="rcInfoItem">
                                <span className="rcInfoLabel">Établissement</span>
                                <span className="rcInfoValue">
                                    {demande.etablissementNom || demande.etabliseement || "—"}
                                </span>
                            </div>

                            <div className="rcInfoItem">
                                <span className="rcInfoLabel">Années d'expérience</span>
                                <span className="rcInfoValue">{demande.anneesExperience || "—"}</span>
                            </div>

                            <div className="rcInfoItem">
                                <span className="rcInfoLabel">Type d'emploi</span>
                                <span className="rcInfoValue">{demande.typeEmploi || "—"}</span>
                            </div>

                            {/*<div className="rcInfoItem rcInfoItem--wide">*/}
                            {/*    <span className="rcInfoLabel">Profil recherché</span>*/}
                            {/*    <span className="rcInfoValue rcInfoValue--multiline">*/}
                            {/*        {demande.profilRecherche || demande.profileRecherche || "—"}*/}
                            {/*    </span>*/}
                            {/*</div>*/}

                            {/*<div className="rcInfoItem rcInfoItem--wide">*/}
                            {/*    <span className="rcInfoLabel">Description</span>*/}
                            {/*    <span className="rcInfoValue rcInfoValue--multiline">*/}
                            {/*        {demande.description || "—"}*/}
                            {/*    </span>*/}
                            {/*</div>*/}

                            {/*<div className="rcInfoItem rcInfoItem--wide">*/}
                            {/*    <span className="rcInfoLabel">Descriptif de poste</span>*/}
                            {/*    <span className="rcInfoValue rcInfoValue--multiline">*/}
                            {/*        {demande.descriptifPoste || demande.descriptifposte || "—"}*/}
                            {/*    </span>*/}
                            {/*</div>*/}
                        </div>
                    </div>

                    <div className="rcSection">
                        <div className="rcSection__title">
                            <Users size={16} />
                            Population cible
                        </div>

                        <div className="cr-filters-inline">
                            <div className="cr-filter-group">
                                <span className="cr-filter-group__label">Mode de filtrage</span>
                                <div className="cr-filter-group__options">
                                    {[
                                        { value: "IN", label: "IN" },
                                        { value: "NOT_IN", label: "NOT IN" },
                                    ].map((opt) => (
                                        <label key={opt.value} className="cr-pill-radio">
                                            <input
                                                type="radio"
                                                name="filterModeRecCoop"
                                                value={opt.value}
                                                checked={filterMode === opt.value}
                                                onChange={() => setFilterMode(opt.value)}
                                            />
                                            <span className={`cr-pill${filterMode === opt.value ? " active" : ""}`}>
                                                {opt.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="cr-form-grid" style={{ marginTop: 14 }}>
                            <div className="cr-field">
                                <MultiSelectDropdown
                                    label="Entités"
                                    items={entites || []}
                                    value={entiteIds}
                                    onChange={setEntiteIds}
                                    placeholder={
                                        entiteIds.length > 0
                                            ? `${entiteIds.length} sélectionnée(s)`
                                            : "Sélectionner..."
                                    }
                                    getId={(x) => +x.id}
                                    getLabel={(x) => x.nom || x.name || `#${x.id}`}
                                />

                                {entiteIds.length > 0 && (
                                    <div className="cr-selected-tags">
                                        {entiteIds.map((id) => (
                                            <span key={id} className="cr-selected-tag">
                                                {getEntiteName(id)}
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setEntiteIds((prev) => prev.filter((x) => x !== id))
                                                    }
                                                >
                                                    <X size={9} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="cr-field">
                                <MultiSelectDropdown
                                    label="Postes"
                                    items={jobs || []}
                                    value={posteIds}
                                    onChange={setPosteIds}
                                    placeholder={
                                        posteIds.length > 0
                                            ? `${posteIds.length} sélectionné(s)`
                                            : "Sélectionner..."
                                    }
                                    getId={(x) => +x.id}
                                    getLabel={(x) => x.nom || x.name || `#${x.id}`}
                                />

                                {posteIds.length > 0 && (
                                    <div className="cr-selected-tags">
                                        {posteIds.map((id) => (
                                            <span key={id} className="cr-selected-tag">
                                                {getPosteName(id)}
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setPosteIds((prev) => prev.filter((x) => x !== id))
                                                    }
                                                >
                                                    <X size={9} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="cr-collab-toggle" style={{ marginTop: 16 }}>
                            <div className="cr-collab-toggle__info">
                                <div className="cr-strong" style={{ fontSize: 12 }}>
                                    Collaborateurs
                                </div>
                                <div className="cr-hint">
                                    {selectedUsers.length > 0
                                        ? `${selectedUsers.length} collaborateur${
                                            selectedUsers.length > 1 ? "s" : ""
                                        } sélectionné${selectedUsers.length > 1 ? "s" : ""}`
                                        : "Aucun collaborateur sélectionné"}
                                </div>
                            </div>

                            <button
                                type="button"
                                className={`cr-btn${showUsersPanel ? " cr-btn--ghost" : " cr-btn--primary"}`}
                                onClick={() => setShowUsersPanel((p) => !p)}
                            >
                                <Users size={14} />
                                {showUsersPanel ? "Masquer" : "Afficher les collaborateurs"}
                                {showUsersPanel ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                            </button>
                        </div>

                        {showUsersPanel && (
                            <InlineUsersPanel
                                users={users || []}
                                usersState={usersState}
                                setUsersState={setUsersState}
                                setSelectedUsers={setSelectedUsers}
                                entiteIds={entiteIds}
                                posteIds={posteIds}
                            />
                        )}
                    </div>

                    <div className="rcSection">
                        {/*<div className="rcSection__title">*/}
                        {/*    {activeMode === "recommandation" ? <Mail size={16} /> : <UserPlus size={16} />}*/}
                        {/*    {activeMode === "recommandation"*/}
                        {/*        ? "Contenu — Email de Recommandation"*/}
                        {/*        : "Contenu — Email de Cooptation"}*/}
                        {/*</div>*/}

                        {/*<div className="rcCardText">*/}
                        {/*    <p className="rcDescText">*/}
                        {/*        {activeMode === "recommandation"*/}
                        {/*            ? "Envoyez un email aux collaborateurs pour leur demander de recommander des candidats qualifiés pour ce poste. L'email contiendra les détails du poste et un lien pour soumettre des recommandations."*/}
                        {/*            : "Envoyez un email aux collaborateurs pour les inviter à coopter des candidats de leur réseau. Ils recevront les détails du poste et pourront proposer directement des profils intéressants."}*/}
                        {/*    </p>*/}
                        {/*</div>*/}
                    </div>

                    <div className="rcSection">
                        <div className="rcSection__title">
                            <Calendar size={16} />
                            Planification de l'envoi
                        </div>

                        <div className="rcGrid">
                            <div className="rcField">
                                <label>Date d'envoi de l'email</label>
                                <input
                                    type="date"
                                    value={envoiDate}
                                    onChange={(e) => setEnvoiDate(e.target.value)}
                                    min={new Date().toISOString().split("T")[0]}
                                />
                            </div>

                            <div className="rcField">
                                <label>Date limite de réponse</label>
                                <input
                                    type="date"
                                    value={limitDate}
                                    onChange={(e) => setLimitDate(e.target.value)}
                                    min={envoiDate}
                                />
                            </div>
                        </div>

                        <div className="rcInfoBox">
                            <strong>📅 Récapitulatif :</strong> L'email sera envoyé le{" "}
                            <strong>{new Date(envoiDate).toLocaleDateString("fr-FR")}</strong> et les
                            collaborateurs auront jusqu'au{" "}
                            <strong>{new Date(limitDate).toLocaleDateString("fr-FR")}</strong> pour répondre.
                        </div>
                    </div>
                </div>

                <div className="rcModal__footer">
                    <button className="rcBtn rcBtn--ghost" onClick={onClose} type="button">
                        Annuler
                    </button>

                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="rcBtn rcBtn--secondary" onClick={handleSave} type="button">
                            <Save size={16} />
                            Planifier
                        </button>

                        <button className="rcBtn rcBtn--primary" onClick={handleSendNow} type="button">
                            <Send size={16} />
                            Envoyer maintenant
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RecommandationCooptationModal;